import { supabase } from './supabase';

const assignmentSelect = `
  id,
  matter_id,
  assigned_to_client_id,
  assigned_by_user_id,
  assignment_type,
  note,
  status,
  due_date,
  completed_at,
  created_at,
  updated_at,
  topic:education_topics(id, title, slug, content_category, short_summary, plain_english_explanation),
  resource:education_resources(id, title, slug, resource_type, short_description, estimated_minutes),
  track:education_learning_tracks(id, title, slug, description, content_category)
`;

const trackItemSelect = `
  id,
  track_id,
  title_override,
  description_override,
  display_priority,
  topic:education_topics(id, title, slug, content_category, short_summary, plain_english_explanation),
  resource:education_resources(id, title, slug, resource_type, short_description, estimated_minutes)
`;

const toSingle = (value) => Array.isArray(value) ? value[0] : value;

export const normalizeAssignedLearningItem = (assignment) => ({
  ...assignment,
  topic: toSingle(assignment.topic),
  resource: toSingle(assignment.resource),
  track: toSingle(assignment.track),
  track_items: assignment.track_items || [],
  total_item_count: assignment.total_item_count || 0,
  completed_item_count: assignment.completed_item_count || 0
});

export const fetchAssignedLearning = async ({ matterIds = [] }) => {
  const ids = matterIds.filter(Boolean);
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from('education_learning_assignments')
    .select(assignmentSelect)
    .in('matter_id', ids)
    .order('created_at', { ascending: false });

  if (error) throw error;

  const assignments = data || [];
  const trackIds = Array.from(new Set(
    assignments
      .filter((assignment) => assignment.assignment_type === 'track' && toSingle(assignment.track)?.id)
      .map((assignment) => toSingle(assignment.track).id)
  ));

  let itemsByTrackId = {};
  if (trackIds.length > 0) {
    const { data: trackItems, error: trackItemsError } = await supabase
      .from('education_learning_track_items')
      .select(trackItemSelect)
      .in('track_id', trackIds)
      .eq('is_active', true)
      .order('display_priority', { ascending: true });

    if (trackItemsError) throw trackItemsError;

    itemsByTrackId = (trackItems || []).reduce((map, item) => {
      map[item.track_id] = [
        ...(map[item.track_id] || []),
        {
          ...item,
          topic: toSingle(item.topic),
          resource: toSingle(item.resource)
        }
      ];
      return map;
    }, {});
  }

  const trackAssignmentIds = assignments
    .filter((assignment) => assignment.assignment_type === 'track')
    .map((assignment) => assignment.id);

  let completedItemIdsByAssignmentId = {};
  if (trackAssignmentIds.length > 0) {
    const { data: progressRows, error: progressError } = await supabase
      .from('education_learning_assignment_item_progress')
      .select('assignment_id, track_item_id')
      .in('assignment_id', trackAssignmentIds);

    if (progressError) throw progressError;

    completedItemIdsByAssignmentId = (progressRows || []).reduce((map, row) => {
      if (!map[row.assignment_id]) map[row.assignment_id] = new Set();
      map[row.assignment_id].add(row.track_item_id);
      return map;
    }, {});
  }

  return assignments.map((assignment) => {
    const track = toSingle(assignment.track);
    const completedItemIds = completedItemIdsByAssignmentId[assignment.id] || new Set();
    const trackItems = (track?.id ? itemsByTrackId[track.id] || [] : []).map((item) => ({
      ...item,
      completed: completedItemIds.has(item.id)
    }));

    return normalizeAssignedLearningItem({
      ...assignment,
      track_items: trackItems,
      total_item_count: trackItems.length,
      completed_item_count: trackItems.filter((item) => item.completed).length
    });
  });
};

export const fetchLearningCatalog = async () => {
  const [topicsRes, resourcesRes, tracksRes] = await Promise.all([
    supabase
      .from('education_topics')
      .select('id, title, slug, content_category, short_summary, display_priority')
      .eq('publication_status', 'published')
      .order('display_priority', { ascending: true })
      .order('title', { ascending: true }),
    supabase
      .from('education_resources')
      .select('id, title, slug, resource_type, short_description, estimated_minutes')
      .eq('is_active', true)
      .eq('publication_status', 'published')
      .order('title', { ascending: true }),
    supabase
      .from('education_learning_tracks')
      .select('id, title, slug, description, content_category, display_priority')
      .eq('is_active', true)
      .order('display_priority', { ascending: true })
      .order('title', { ascending: true })
  ]);

  if (topicsRes.error) throw topicsRes.error;
  if (resourcesRes.error) throw resourcesRes.error;
  if (tracksRes.error) throw tracksRes.error;

  return {
    topics: topicsRes.data || [],
    resources: resourcesRes.data || [],
    tracks: tracksRes.data || []
  };
};

export const createLearningAssignment = async ({
  matter,
  assignedByUserId,
  assignmentType,
  itemId,
  note = '',
  dueDate = ''
}) => {
  if (!matter?.id || !matter?.client_id || !assignedByUserId || !assignmentType || !itemId) {
    throw new Error('Choose a learning item before assigning it.');
  }

  const payload = {
    matter_id: matter.id,
    assigned_to_client_id: matter.client_id,
    assigned_by_user_id: assignedByUserId,
    assignment_type: assignmentType,
    note: note.trim() || null,
    due_date: dueDate || null,
    topic_id: assignmentType === 'topic' ? itemId : null,
    resource_id: assignmentType === 'resource' ? itemId : null,
    track_id: assignmentType === 'track' ? itemId : null
  };

  const { data, error } = await supabase
    .from('education_learning_assignments')
    .insert(payload)
    .select(assignmentSelect)
    .single();

  if (error) throw error;
  return normalizeAssignedLearningItem(data);
};

export const completeLearningAssignment = async (assignmentId) => {
  if (!assignmentId) return null;

  const { data, error } = await supabase.rpc('complete_education_learning_assignment', {
    p_assignment_id: assignmentId
  });

  if (error) throw error;
  return data;
};

export const setTrackItemCompletion = async ({ assignmentId, trackItemId, completed }) => {
  if (!assignmentId || !trackItemId) return null;

  const { data, error } = await supabase.rpc('complete_education_learning_track_item', {
    p_assignment_id: assignmentId,
    p_track_item_id: trackItemId,
    p_completed: completed
  });

  if (error) throw error;
  return data;
};
